import {
  Component,
  signal,
  OnInit,
  AfterViewInit,
  inject,
  DestroyRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { DEFAULT_LANGUAGE, AVAILABLE_LANGUAGES } from './core/constants';
import { PreferencesConfig, DEFAULT_PREFERENCES } from './models/preferences';
import { KeyboardService } from './core/services/keyboard.service';
import { ShortcutsService } from './core/services/shortcuts.service';
import { ToastModule } from 'primeng/toast';
import {
  ToastService,
  AuthService,
  UserService,
  CollabWebSocketService,
  WebSocketMessage,
  CollabService,
  MessengerUnreadService,
} from './core/services';
import { MessageService } from 'primeng/api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { DynamicDialogService } from './shared/components';
import { PermissionStore, BalanceStore } from './core/stores';
import { environment } from '../environments/environment';
import { PushService } from './core/services/push.service';
// import { SwNotifyService } from './core/services/sw-notify.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, ToastModule],
  template: ` <router-outlet />
    <p-toast
      position="bottom-left"
      [breakpoints]="{ '920px': { width: '90%', right: '.75rem', left: '.75rem' } }"
    />
    <div #dialogHost data-dialog-host></div>`,
  providers: [MessageService],
})
export class App implements OnInit, AfterViewInit {
  protected readonly title = signal('fara-chat');
  @ViewChild('dialogHost', { read: ViewContainerRef, static: false }) dialogHost!: ViewContainerRef;

  private translocoService = inject(TranslocoService);
  //dont remove this below services
  private keyboardService = inject(KeyboardService);
  private shortcutsService = inject(ShortcutsService);
  private readonly messageService = inject(MessageService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly authService = inject(AuthService);
  private readonly userService = inject(UserService);
  private readonly dynamicDialogService = inject(DynamicDialogService);
  private readonly permissionStore = inject(PermissionStore);
  private readonly balanceStore = inject(BalanceStore);
  private readonly collabWebSocket = inject(CollabWebSocketService);
  private readonly usersChatService = inject(CollabService);
  private readonly messengerUnreadService = inject(MessengerUnreadService);

  // Debounce timer for unread messages updates
  private unreadUpdateTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly UNREAD_UPDATE_DEBOUNCE_MS = 1000; // Update at most once per second

  push = inject(PushService);
  enable() {
    this.push.init();
  }
  test() {
    console.log('hi');
    this.push.showTestNotification();
  }

  ngOnInit() {
    this.initializeLanguage();
    this.initializeUserPreferences();
    this.initializeShortcuts();
    this.initializeUserProfile();
    this.initializePermissions();
    this.initializeBalance();
    // Only initialize WebSocket in build mode and not on localhost
    // if (this.shouldInitializeWebSocket()) {
    //   this.initializeCollabWebSocket();
    //   this.loadCollabUnreadMessages();
    // }
    // Subscribe to toast messages from the service
    this.toastService.toast$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((toastMessage) => {
      if (toastMessage) {
        this.messageService.add(toastMessage);
      }
    });
    // Hide loading skeleton when fonts and resources are loaded
    this.hideLoadingSkeleton();

    // Cleanup timer on destroy
    this.destroyRef.onDestroy(() => {
      if (this.unreadUpdateTimer) {
        clearTimeout(this.unreadUpdateTimer);
        this.unreadUpdateTimer = null;
      }
    });
  }

  ngAfterViewInit(): void {
    // Initialize dynamic dialog service after view is initialized
    // Use setTimeout to ensure ViewChild is available after change detection
    // Multiple attempts in case of timing issues with router
    const initializeDialogHost = () => {
      if (this.dialogHost) {
        this.dynamicDialogService.setDialogHost(this.dialogHost);
        console.log('Dialog host initialized successfully');
      } else {
        console.warn('Dialog host ViewChild not available yet, retrying...');
        setTimeout(initializeDialogHost, 10);
      }
    };

    setTimeout(initializeDialogHost, 0);
  }

  /**
   * Initialize keyboard shortcuts for the application
   * This method ensures that global shortcuts are available throughout the app
   */
  private initializeShortcuts() {
    try {
      // The KeyboardService automatically initializes when injected
      // It will load shortcuts from the ShortcutsService and start listening
      // Optional: Subscribe to shortcut events if needed for app-level handling
      // this.keyboardService.toggleSidebarSubject.subscribe(() => {
      // });
    } catch (error) {
      console.error('Failed to initialize keyboard shortcuts:', error);
    }
  }

  private initializeLanguage() {
    const savedLanguage = localStorage.getItem('selectedLanguage') || DEFAULT_LANGUAGE;
    const language = AVAILABLE_LANGUAGES.find((lang) => lang.code === savedLanguage);

    if (language) {
      this.translocoService.setActiveLang(language.code);

      const direction = language.code === 'fa' || language.code === 'ar' ? 'rtl' : 'ltr';
      document.documentElement.dir = direction;
      document.documentElement.lang = language.code;
    }
  }

  /**
   * Initialize user preferences from localStorage and apply them to the site
   * This function reads FARACHAT_PREFERENCES and applies theme, dark mode, and font size
   */
  private initializeUserPreferences() {
    try {
      // Read preferences from localStorage
      const storedPreferences = this.readUserPreferences();

      if (storedPreferences) {
        // Apply dark mode
        this.applyDarkMode(storedPreferences.darkMode);

        // Apply theme
        this.applyTheme(storedPreferences.theme);

        // Apply font size
        this.applyFontSize(storedPreferences.fontSize);
      } else {
        // Apply default preferences if none found
        this.applyDefaultPreferences();
      }
    } catch (error) {
      console.error('Failed to initialize user preferences:', error);
      this.applyDefaultPreferences();
    }
  }

  /**
   * Read user preferences from localStorage
   * @returns PreferencesConfig or null if not found/invalid
   */
  private readUserPreferences(): PreferencesConfig | null {
    try {
      const stored = localStorage.getItem('FARACHAT_PREFERENCES');
      if (!stored) {
        return null;
      }

      const preferences: PreferencesConfig = JSON.parse(stored);

      // Validate the preferences structure
      if (this.isValidPreferences(preferences)) {
        return preferences;
      } else {
        console.warn('Invalid preferences structure found, using defaults');
        return null;
      }
    } catch (error) {
      console.error('Error parsing user preferences:', error);
      return null;
    }
  }

  /**
   * Validate preferences structure
   * @param preferences - Preferences object to validate
   * @returns boolean indicating if preferences are valid
   */
  private isValidPreferences(preferences: any): preferences is PreferencesConfig {
    return (
      preferences &&
      typeof preferences === 'object' &&
      typeof preferences.darkMode === 'boolean' &&
      typeof preferences.theme === 'string' &&
      ['green', 'peach', 'cyan'].includes(preferences.theme) &&
      typeof preferences.fontSize === 'number' &&
      preferences.fontSize >= 12 &&
      preferences.fontSize <= 24
    );
  }

  /**
   * Apply dark mode to the document
   * @param darkMode - Whether dark mode should be enabled
   */
  private applyDarkMode(darkMode: boolean) {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }

  /**
   * Fetch user profile and persist it into session/localStorage
   * Only runs when a session already exists (user is logged in)
   */
  private initializeUserProfile() {
    const currentSession = this.authService.currentUser;
    if (!currentSession) {
      return;
    }

    this.userService
      .getUserProfile()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (profile) => {
          const updatedSession = { ...currentSession, user: profile };
          this.authService.setUser(updatedSession);
        },
        error: () => {
          // Silently ignore profile load errors on app bootstrap
        },
      });
  }

  /**
   * Load user permissions from API
   * Only runs when a session already exists (user is logged in)
   */
  private initializePermissions() {
    const currentSession = this.authService.currentUser;
    if (!currentSession) {
      return;
    }

    // Load permissions asynchronously (don't block app initialization)
    this.permissionStore.loadPermissions().catch((error) => {
      // Silently ignore permission load errors on app bootstrap
      // Permissions will be retried when directive is used
      console.warn('Failed to load permissions on app initialization:', error);
    });
  }

  /**
   * Load user balance from API
   * Only runs when a session already exists (user is logged in)
   */
  private initializeBalance() {
    const currentSession = this.authService.currentUser;
    if (!currentSession) {
      return;
    }

    // Load balance asynchronously (don't block app initialization)
    this.balanceStore.updateBalance().catch((error) => {
      // Silently ignore balance load errors on app bootstrap
      console.warn('Failed to load balance on app initialization:', error);
    });
  }

  /**
   * Apply theme to the document
   * @param theme - Theme to apply ('green', 'peach', 'cyan')
   */
  private applyTheme(theme: 'green' | 'peach' | 'cyan') {
    // Remove existing theme classes
    document.documentElement.classList.remove('theme-green', 'theme-peach', 'theme-cyan');

    // Add new theme class
    document.documentElement.classList.add(`theme-${theme}`);

    // Set CSS custom property for theme color
    const themeColors = {
      green: '#10B981',
      peach: '#F59E0B',
      cyan: '#06B6D4',
    };

    document.documentElement.style.setProperty('--primary-color', themeColors[theme]);
  }

  /**
   * Apply font size to the document
   * @param fontSize - Font size in pixels
   */
  private applyFontSize(fontSize: number) {
    // Clamp font size to valid range
    const clampedFontSize = Math.max(12, Math.min(24, fontSize));
    document.documentElement.style.setProperty('--base-font-size', `${clampedFontSize}px`);
  }

  /**
   * Apply default preferences to the site
   */
  private applyDefaultPreferences() {
    this.applyDarkMode(DEFAULT_PREFERENCES.darkMode);
    this.applyTheme(DEFAULT_PREFERENCES.theme);
    this.applyFontSize(DEFAULT_PREFERENCES.fontSize);
  }

  /**
   * Hide the loading skeleton when all fonts and resources are loaded
   * This ensures users see the skeleton until everything is ready
   */
  private async hideLoadingSkeleton() {
    try {
      // Wait for fonts to load
      await this.waitForFontsToLoad();

      // Wait for Angular to render (using requestAnimationFrame)
      await new Promise((resolve) => requestAnimationFrame(resolve));

      // Small delay to ensure smooth transition
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Hide the skeleton with fade-out animation
      const skeleton = document.getElementById('app-loading-skeleton');
      const appRoot = document.querySelector('app-root');

      if (skeleton) {
        skeleton.classList.add('hidden');

        // Show app-root with fade-in
        if (appRoot) {
          appRoot.classList.add('loaded');
        }

        // Remove skeleton from DOM after animation completes
        setTimeout(() => {
          skeleton.remove();
        }, 400); // Match the CSS transition duration
      }
    } catch (error) {
      console.error('Error hiding loading skeleton:', error);
      // Fallback: Hide skeleton anyway after a timeout
      setTimeout(() => {
        const skeleton = document.getElementById('app-loading-skeleton');
        const appRoot = document.querySelector('app-root');

        if (skeleton) {
          skeleton.classList.add('hidden');
          setTimeout(() => skeleton.remove(), 400);
        }

        if (appRoot) {
          appRoot.classList.add('loaded');
        }
      }, 1000);
    }
  }

  /**
   * Wait for all fonts to be loaded
   * Uses Font Loading API to detect when fonts are ready
   */
  private async waitForFontsToLoad(): Promise<void> {
    if ('fonts' in document) {
      try {
        // Wait for all fonts to be loaded
        await document.fonts.ready;

        // Additional check for critical fonts
        const criticalFonts = [
          { family: 'IRANSans', weight: '400' },
          { family: 'Font Awesome 6 Pro', weight: '400' },
          { family: 'Font Awesome 6 Brands', weight: '400' },
        ];

        // Check if critical fonts are loaded
        const fontChecks = criticalFonts.map((font) => {
          return document.fonts.check(`${font.weight} 16px "${font.family}"`);
        });

        // If any critical font is not loaded, wait a bit more
        if (!fontChecks.every((loaded) => loaded)) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      } catch (error) {
        console.warn('Font loading check failed:', error);
        // Fallback: wait a fixed time
        await new Promise((resolve) => setTimeout(resolve, 500));
      }
    } else {
      // Fallback for browsers without Font Loading API
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  }

  /**
   * Check if WebSocket should be initialized
   * Only in production mode and not on localhost
   */
  private shouldInitializeWebSocket(): boolean {
    // Check if we're not on localhost
    const hostname = window.location.hostname;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';

    return !isLocalhost;
  }

  // notify = inject(SwNotifyService);

  /**
   * Initialize Collab WebSocket connection for app-wide notifications
   * Only initializes if user is logged in
   */
  private initializeCollabWebSocket() {
    const currentSession = this.authService.currentUser;
    if (!currentSession) {
      // User not logged in, don't initialize WebSocket
      return;
    }

    const collabUrl = environment.collabBaseUrl;

    // Set the Collab URL
    this.collabWebSocket.setCollabUrl(collabUrl);

    // Add message listener to log all received messages and handle posted events
    const messageListener = (msg: WebSocketMessage) => {
      console.log('[Collab WebSocket] Event received:', {
        event: msg.event,
        data: msg.data,
        broadcast: msg.broadcast,
        sequence: msg.seq,
        timestamp: new Date().toISOString(),
      });

      // Update unread counts on websocket messages (debounced to avoid excessive API calls)
      // This ensures the badge is updated in real-time without overwhelming the server
      this.debouncedUpdateUnreadCounts();

      // Handle "posted" event - show toast notification
      if (msg.event === 'posted' && msg.data) {
        try {
          const channelDisplayName = msg.data.channel_display_name || 'Unknown Channel';
          const postDataStr = msg.data.post;

          if (postDataStr) {
            // Parse the post JSON string if it's a string, otherwise use as-is
            let postData: any;
            if (typeof postDataStr === 'string') {
              try {
                postData = JSON.parse(postDataStr);
              } catch (parseError) {
                console.error('[Collab WebSocket] Error parsing post JSON:', parseError);
                return;
              }
            } else {
              postData = postDataStr;
            }

            const postMessage = postData?.message || '';

            if (postMessage) {
              // if (document.visibilityState !== 'visible') {
              //   this.notify.notify(
              //     channelDisplayName || 'Channel',
              //     postMessage,
              //     `/messenger`,
              //     // `/channels/${postData.channel_id}`,
              //   );
              // }
              // Show toast notification with channel name and message
              this.toastService.info(
                channelDisplayName,
                postMessage,
                10000, // Show for 5 seconds
              );
            }
          }
        } catch (error) {
          console.error('[Collab WebSocket] Error handling posted event:', error, msg);
        }
      }
    };

    this.collabWebSocket.addMessageListener(messageListener);

    // Add connection event listeners
    this.collabWebSocket.addFirstConnectListener(() => {
      console.log('[Collab WebSocket] First connection established');
    });

    this.collabWebSocket.addReconnectListener(() => {
      console.log('[Collab WebSocket] Reconnected');
    });

    this.collabWebSocket.addErrorListener((event) => {
      console.error('[Collab WebSocket] Error:', event);
      // Hide badge on websocket error
      this.messengerUnreadService.setError(true);
    });

    this.collabWebSocket.addCloseListener((connectFailCount) => {
      console.log(`[Collab WebSocket] Connection closed (fail count: ${connectFailCount})`);
    });

    // Initialize the WebSocket connection
    // The service will automatically use hardcoded cookies for authentication
    this.collabWebSocket.initialize();
  }

  /**
   * Debounced update of unread counts to avoid excessive API calls
   */
  private debouncedUpdateUnreadCounts() {
    // Clear existing timer
    if (this.unreadUpdateTimer) {
      clearTimeout(this.unreadUpdateTimer);
    }

    // Set new timer
    this.unreadUpdateTimer = setTimeout(() => {
      this.loadCollabUnreadMessages();
      this.unreadUpdateTimer = null;
    }, this.UNREAD_UPDATE_DEBOUNCE_MS);
  }

  /**
   * Load Collab unread messages count and log to console
   */
  private loadCollabUnreadMessages() {
    const currentSession = this.authService.currentUser;
    if (!currentSession) {
      // User not logged in, don't load unread messages
      return;
    }

    // First get all channels
    this.usersChatService
      .getUserChannels()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (channels) => {
          console.log(`[Collab] Found ${channels.length} channels`);

          if (channels.length === 0) {
            console.log('[Collab] Total unread messages: 0');
            this.messengerUnreadService.setUnreadCounts(0, 0);
            return;
          }

          // Get unread count for each channel
          const unreadRequests = channels.map((channel) =>
            this.usersChatService.getChannelUnread(channel.id).pipe(
              map((unread) => ({
                channelId: channel.id,
                channelName: channel.display_name || channel.name,
                unreadCount: unread.msg_count || 0,
                mentionCount: unread.mention_count || 0,
              })),
            ),
          );

          // Execute all requests in parallel
          forkJoin(unreadRequests)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (unreadData) => {
                // Log each channel's unread count
                unreadData.forEach((data) => {
                  const hasUnread = data.unreadCount > 0 || data.mentionCount > 0;
                  if (hasUnread) {
                    console.log(
                      `[Collab] Channel "${data.channelName}" (${data.channelId}): ${data.unreadCount} unread messages, ${data.mentionCount} unread mentions`,
                    );
                  }
                });

                // unread message and mention counts
                const totalMessages = unreadData.reduce((sum, data) => {
                  return sum + data.unreadCount;
                }, 0);

                const totalMentions = unreadData.reduce((sum, data) => {
                  return sum + data.mentionCount;
                }, 0);

                console.log(
                  `[Collab] Total unread across all channels: ${totalMessages} messages (${totalMentions} mentions)`,
                );

                // Update the service with unread counts
                this.messengerUnreadService.setUnreadCounts(totalMessages, totalMentions);
              },
              error: (error) => {
                console.error('[Collab] Error getting unread counts:', error);
                // Hide badge on error
                this.messengerUnreadService.setError(true);
              },
            });
        },
        error: (error) => {
          console.error('[Collab] Error loading channels:', error);
          // Hide badge on error
          this.messengerUnreadService.setError(true);
        },
      });
  }
}
